import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/hooks/useLocale';
import { Button } from '@/components/ui/button';
import { Save, Download, GitCompareArrows } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { CURRENCIES, INDUSTRY_DURATION_FACTORS, MATURITY_DURATION_FACTORS, getEffectiveMaturity, interpolateFactor } from '@/lib/calculationEngine';
import { generateEstimatePDF } from '@/lib/pdfExport';
import { addDraft } from '@/lib/scenarioDrafts';
import { EstimateHeadline } from './results/EstimateHeadline';
import { MetricsStrip } from './results/MetricsStrip';
import { CostBreakdownChart } from './results/CostBreakdownChart';
import { CostDriversPanel } from './results/CostDriversPanel';
import { MethodologyPanel } from './results/MethodologyPanel';
import { AssumptionsPanel } from '@/components/trust/AssumptionsPanel';
import { PhaseTimeline } from './results/PhaseTimeline';
import { SaveEstimateDialog } from './results/SaveEstimateDialog';
import { ConfidenceIndicator } from '@/components/trust/ConfidenceIndicator';
import logger from '@/lib/logger';

const API = '/api';

/** Duration overhead pills (industry + maturity multipliers). */
function useDurationHints(config) {
  const { t } = useTranslation();
  return useMemo(() => {
    const hints = [];
    const industryFactor = INDUSTRY_DURATION_FACTORS[config.industry] || 1.0;
    const maturityLevel = getEffectiveMaturity(config);
    const maturityFactor = interpolateFactor(MATURITY_DURATION_FACTORS, maturityLevel);

    if (industryFactor > 1.0) {
      const industryLabel = config.industry ? t(`calcData.industries.${config.industry}`) : '';
      const shortLabel = industryLabel.split(' ')[0];
      hints.push(`${shortLabel} +${Math.round((industryFactor - 1) * 100)}%`);
    }
    const maturityPct = Math.round((maturityFactor - 1) * 100);
    if (maturityPct !== 0) {
      const sign = maturityPct > 0 ? '+' : '';
      hints.push(`L${Math.round(maturityLevel * 10) / 10} ${sign}${maturityPct}%`);
    }
    return hints;
  }, [config, t]);
}

export function ResultsPanel({ results, config, loadedEstimateName, onEditAssumption, tco, tcoOverrides }) {
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { localePath } = useLocale();

  const isDemo = user?.is_demo;
  const symbol = CURRENCIES.find(c => c.value === config.currency)?.symbol || '$';
  const formatCost = (val) => `${symbol}${Number(val || 0).toLocaleString()}`;
  const durationHints = useDurationHints(config);

  const openSaveDialog = () => {
    if (!user) {
      navigate(localePath('/auth'), { state: { from: localePath('/calculator') } });
      return;
    }
    if (isDemo) {
      toast.info(t('results.demoSaveHint'), {
        action: { label: t('results.signUp'), onClick: () => navigate(localePath('/auth')) },
      });
      return;
    }
    setSaveName(loadedEstimateName ? `${loadedEstimateName} (copy)` : '');
    setSaveOpen(true);
  };

  const handleSave = async () => {
    if (!saveName.trim()) return;
    setSaving(true);
    try {
      // The browser result is a preview: the backend recalculates from `config`
      // and its result is authoritative.
      const { data } = await axios.post(`${API}/estimates`, {
        name: saveName, config, results, tco: tcoOverrides,
      }, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      });
      toast.success(t('results.saved'), {
        action: { label: t('results.viewSaved'), onClick: () => navigate(localePath('/estimates')) },
      });
      if (data?.material_discrepancy) {
        toast.info(t('trust.recalcNotice'), { duration: 8000 });
      }
      setSaveOpen(false);
      setSaveName('');
    } catch (err) {
      logger.warn('[ResultsPanel] Save failed:', err?.message || err);
      toast.error(t('results.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleExportPdf = () => {
    generateEstimatePDF({ name: loadedEstimateName || 'Current Estimate', config, results, tco, t });
    toast.success(t('results.pdfDownloaded'));
  };

  const handleCompareScenario = () => {
    const fallback = `${t(`calcData.sizes.${config.companySize}`)} · ${t(`calcData.industries.${config.industry}`)}`;
    addDraft({ name: loadedEstimateName || fallback, config, tco: tcoOverrides });
    toast.success(t('scenarios.added'));
    navigate(localePath('/scenarios'));
  };

  let saveLabel;
  if (!user) saveLabel = t('results.signInToSave');
  else if (loadedEstimateName) saveLabel = t('results.saveAsNew');
  else saveLabel = t('results.saveEstimate');

  return (
    <div className="space-y-4" data-testid="results-panel" aria-label={t('results.title')}>
      <EstimateHeadline
        results={results}
        config={config}
        formatCost={formatCost}
        name={loadedEstimateName}
        legacy={!results.engineVersion && !results.methodology}
      />
      <MetricsStrip results={results} durationHints={durationHints} />
      <CostBreakdownChart costBreakdown={results.costBreakdown} formatCost={formatCost} />
      {results.drivers && <CostDriversPanel drivers={results.drivers} formatCost={formatCost} />}
      <PhaseTimeline phases={results.phases} />
      <ConfidenceIndicator confidence={results.confidence} assumptions={results.assumptions} />
      <AssumptionsPanel
        assumptions={results.assumptions}
        formatCost={formatCost}
        config={config}
        onEdit={onEditAssumption}
        onReview={() => {
          document
            .querySelector('[data-testid="company-profile-section"]')
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      />
      <MethodologyPanel results={results} config={config} formatCost={formatCost} />

      {/* Action buttons */}
      <div className="flex gap-3">
        <Button
          className="flex-1 h-11 bg-[#527F19] hover:bg-[#3F6114] text-white font-medium rounded-md"
          onClick={openSaveDialog}
          data-testid="save-estimate-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saveLabel}
        </Button>
        <Button
          variant="outline"
          className="h-11 border-[#DDE1E5] text-[#66707A] hover:text-[#171A1D] hover:bg-[#F5F6F7] rounded-md"
          onClick={handleExportPdf}
          data-testid="export-pdf-btn"
          aria-label={t('results.exportPdf', 'Export as PDF')}
          title={t('results.exportPdf', 'Export as PDF')}
        >
          <Download className="w-4 h-4" aria-hidden="true" />
        </Button>
      </div>

      <Button
        variant="outline"
        className="w-full h-10 border-[#DDE1E5] text-[#3A4249] hover:text-[#171A1D] hover:bg-[#F5F6F7] rounded-md"
        onClick={handleCompareScenario}
        data-testid="compare-scenario-btn"
      >
        <GitCompareArrows className="w-4 h-4 mr-2" aria-hidden="true" />
        {t('scenarios.addCurrent')}
      </Button>

      <SaveEstimateDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        saveName={saveName}
        setSaveName={setSaveName}
        loadedEstimateName={loadedEstimateName}
        saving={saving}
        onSave={handleSave}
      />
    </div>
  );
}
